/* assets/section-combo-filter.js
   Combined tag + category filtering on the combo grid, plus scroll
   pagination (Section Rendering API) and the "Find Your Combo" banner.
   Only page 1 ships in the initial HTML; remaining pages are fetched
   on scroll and appended. Filters load all remaining pages once so
   results stay accurate against the full collection.
*/
window.initComboFilter = function (sectionWrapperId) {
  const root = document.getElementById(sectionWrapperId);
  if (!root || root.dataset.comboFilterBound === 'true') return;
  root.dataset.comboFilterBound = 'true';

  const grid = root.querySelector('[data-combo-grid]');
  const tabs = root.querySelectorAll('.combo-filter__tab');
  const categoryButtons = root.querySelectorAll('.combo-filter__category');
  const comboCountLabel = root.querySelector('.combo-filter__count-label');
  const countEl = root.querySelector('[data-combo-count]');
  const emptyEl = root.querySelector('[data-combo-empty]');
  const loaderEl = root.querySelector('[data-combo-loader]');

  /* ---------- banner refs: collect EVERY copy (desktop + mobile) ---------- */
  const BANNER_SELECTOR = '[data-combo-banner], .combo-banner';

  function collectBannerEls() {
    const pieces = [];
    root.querySelectorAll(BANNER_SELECTOR).forEach((b) => {
      const item = b.closest('.grid__item') || b;
      if (!pieces.includes(item)) {
        item.setAttribute('data-combo-banner', ''); // so counts/sentinel skip it
        pieces.push(item);
      }
    });
    return pieces;
  }

  const bannerEls = collectBannerEls();

  function setBannersVisible(visible) {
    bannerEls.forEach((el) => {
      if (visible) {
        // remove inline display so theme CSS decides desktop vs mobile copy
        el.style.removeProperty('display');
        el.hidden = false;
      } else {
        // !important beats any responsive/media-query CSS on mobile
        el.style.setProperty('display', 'none', 'important');
      }
    });
  }

  /* ---------- pagination state ---------- */
  const sectionId = root.dataset.sectionId;
  const collectionUrl = root.dataset.collectionUrl || '';
  const totalProducts = parseInt(root.dataset.totalProducts, 10) || 0;
  const totalPages = grid ? (parseInt(grid.dataset.totalPages, 10) || 1) : 1;
  let nextPage = grid ? ((parseInt(grid.dataset.currentPage, 10) || 1) + 1) : 2;
  let allPagesLoaded = nextPage > totalPages;
  let currentFetch = null; // the in-flight loadNextPage promise, if any
  let observer = null;

  let activeTag = 'all';
  const initialTagBtn = root.querySelector('.combo-filter__tab.is-active');
  if (initialTagBtn) activeTag = initialTagBtn.getAttribute('data-tag');

  let activeCategory = 'all';
  const initialCategoryBtn = root.querySelector('.combo-filter__category.is-active');
  if (initialCategoryBtn) activeCategory = initialCategoryBtn.getAttribute('data-category');

  function getCards() {
    // combo-filter-banner.liquid embeds a hidden library of product
    // cards (#quizPopup .card-product-print) for the "Find Your Combo"
    // quiz — same class/data-product-tags as real grid cards, so it
    // must be excluded or it gets double-counted once a filter is active.
    return Array.from(root.querySelectorAll('.card-product-print'))
      .filter((card) => !card.closest('.quiz-popup-container'));
  }

  function isUnfiltered() {
    return activeTag === 'all' && activeCategory === 'all';
  }

  function setLoading(state) {
    if (loaderEl) loaderEl.hidden = !state;
  }

  function fadeIn(el, delayIndex) {
    // Cap the stagger so a big result set doesn't push the last card's
    // reveal a full second or more out — keeps the effect without making
    // large filters feel slow.
    const delay = Math.min((delayIndex || 0) * 40, 240);
    requestAnimationFrame(() => {
      el.style.transitionDelay = `${delay}ms`;
      el.classList.add('is-revealed');
    });
  }

  function clearActiveCategory() {
    categoryButtons.forEach((b) => {
      b.classList.remove('is-active');
      b.setAttribute('aria-selected', 'false');
    });
    activeCategory = 'all';
  }

  /* ---------- ORIGINAL filter logic (matching rules unchanged) ---------- */
  function applyFilter() {
    let visibleCount = 0;
    let fadeDelay = 0;

    getCards().forEach((card) => {
      const cardTags = (card.getAttribute('data-product-tags') || '')
        .split(',')
        .map((t) => t.trim());
      const matchesTag = activeTag === 'all' || cardTags.includes(activeTag);
      const matchesCategory = activeCategory === 'all' || cardTags.includes(activeCategory);
      const matches = matchesTag && matchesCategory;

      const gridItem = card.closest('.grid__item') || card;

      if (matches) {
        const needsReveal = gridItem.hidden || !gridItem.classList.contains('is-revealed');
        gridItem.hidden = false;
        if (needsReveal) {
          gridItem.classList.remove('is-revealed');
          gridItem.style.transitionDelay = '';
          fadeIn(gridItem, fadeDelay++);
        }
        visibleCount += 1;
      } else {
        gridItem.hidden = true;
      }
    });

    const displayCount = (isUnfiltered() && !allPagesLoaded) ? totalProducts : visibleCount;

    if (countEl) countEl.textContent = displayCount;
    if (emptyEl) emptyEl.hidden = displayCount !== 0;

    if (comboCountLabel) {
      comboCountLabel.textContent = displayCount > 0 ? 'Combos' : 'Combo';
    }

    // Banner: visible ONLY on "All" with no category, and only if
    // there are products to show. No DOM moving — Liquid's position
    // (after the Nth card) is already correct in the "All" order.
    setBannersVisible(isUnfiltered() && displayCount > 0);

    if (isUnfiltered() && !allPagesLoaded) {
      observeSentinel();
    } else if (observer) {
      observer.disconnect();
      observer = null;
    }
  }

  /* ---------- fetch next page via Section Rendering API ---------- */
  function buildPageUrl(page) {
    const joiner = collectionUrl.includes('?') ? '&' : '?';
    return `${collectionUrl}${joiner}section_id=${encodeURIComponent(sectionId)}&page=${page}`;
  }

  function fetchPage(page) {
    return fetch(buildPageUrl(page))
      .then((res) => {
        if (!res.ok) throw new Error(`Combo filter: page fetch failed (${res.status})`);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.querySelector('[data-combo-grid]');
        if (!newGrid || !grid) return [];

        const appended = [];
        Array.from(newGrid.children).forEach((child) => {
          // strip duplicate banner copies from fetched pages —
          // paginate resets forloop.index per page, so every page
          // brings its own copy of the banner otherwise
          if (child.nodeType === 1 && (
              child.hasAttribute('data-combo-banner') ||
              child.matches(BANNER_SELECTOR) ||
              child.querySelector('.combo-banner') ||
              // the quiz popup's hidden card library is a sibling of
              // .combo-banner, not a descendant — strip it separately
              // so it isn't duplicated into the grid on every page
              child.matches('.quiz-popup-container')
          )) {
            return;
          }

          const node = document.adoptNode(child);
          grid.appendChild(node);
          if (node.classList?.contains('grid__item')) {
            appended.push(node);
          }
        });
        return appended;
      });
  }

  function loadNextPage() {
    // if a fetch is already running, return THAT promise instead of
    // a resolved empty one — callers can genuinely wait on it.
    if (currentFetch) return currentFetch;
    if (allPagesLoaded) return Promise.resolve([]);

    setLoading(true);
    const page = nextPage;

    currentFetch = fetchPage(page)
      .then((appended) => {
        nextPage = page + 1;
        allPagesLoaded = nextPage > totalPages;

        let fadeDelay = 0;
        appended.forEach((gridItem) => {
          const card = gridItem.querySelector('.card-product-print');
          const cardTags = card
            ? (card.getAttribute('data-product-tags') || '').split(',').map((t) => t.trim())
            : [];
          const matchesTag = activeTag === 'all' || cardTags.includes(activeTag);
          const matchesCategory = activeCategory === 'all' || cardTags.includes(activeCategory);

          if (matchesTag && matchesCategory) {
            gridItem.hidden = false;
            fadeIn(gridItem, fadeDelay++);
          } else {
            gridItem.hidden = true;
          }
        });

        if (allPagesLoaded) applyFilter();

        return appended;
      })
      .catch((err) => {
        console.error(err);
        allPagesLoaded = true; // stop retry-hammering on error
        return [];
      })
      .finally(() => {
        currentFetch = null;
        setLoading(false);
        if (isUnfiltered() && !allPagesLoaded) {
          observeSentinel();
        } else if (observer) {
          observer.disconnect();
          observer = null;
        }
      });

    return currentFetch;
  }

  function loadAllRemainingPages() {
    // hard safety cap: can never loop more times than pages exist
    let safety = totalPages + 2;

    function step() {
      if (allPagesLoaded || safety-- <= 0) return Promise.resolve();
      return loadNextPage().then(() => {
        // Reconcile the count/empty-state as each page streams in instead
        // of only once at the very end — the grid stays live and correct
        // the whole time instead of jumping all at once when it's done.
        applyFilter();
        return step();
      });
    }
    return step();
  }

  function ensureLoadedThenApply() {
    // Show the correct result for whatever's already loaded immediately —
    // don't leave the previous filter's results on screen while the rest
    // of the collection streams in in the background.
    applyFilter();

    if (isUnfiltered() || allPagesLoaded) return;

    setLoading(true);
    loadAllRemainingPages();
  }

  /* ---------- infinite-scroll sentinel ---------- */
  function observeSentinel() {
    if (observer) observer.disconnect();
    if (allPagesLoaded || !grid) return;

    const items = Array.from(grid.querySelectorAll('.grid__item'))
      .filter((i) => !i.hidden && !i.hasAttribute('data-combo-banner'));
    const sentinel = items[items.length - 1];
    if (!sentinel) return;

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadNextPage();
          }
        });
      },
      { rootMargin: '400px 0px' }
    );

    observer.observe(sentinel);
  }

  /* ---------- ORIGINAL tab handling ---------- */
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      tabs.forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');

      activeTag = tab.getAttribute('data-tag');

      if (activeTag === 'all') {
        clearActiveCategory();
      }

      ensureLoadedThenApply();
    });
  });

  /* ---------- ORIGINAL category handling ---------- */
  categoryButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const alreadyActive = btn.classList.contains('is-active');

      categoryButtons.forEach((b) => {
        b.classList.remove('is-active');
        b.setAttribute('aria-selected', 'false');
      });

      if (alreadyActive) {
        activeCategory = 'all';
      } else {
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        activeCategory = btn.getAttribute('data-category');
      }

      btn.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });

      ensureLoadedThenApply();
    });
  });

  // Run once on load.
  applyFilter();
};
